import { FC, useMemo, ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers';

import { getBuyerProtectionDuration } from './getBuyerProtectionDuration';

import {
    useCoreBuyerIncentives_viewer$key,
    useCoreBuyerIncentives_viewer$data,
} from './__generated__/useCoreBuyerIncentives_viewer.graphql';
import {
    useCoreBuyerIncentives_item$key,
    useCoreBuyerIncentives_item$data,
} from './__generated__/useCoreBuyerIncentives_item.graphql';
import { useCoreBuyerIncentives_user$key } from './__generated__/useCoreBuyerIncentives_user.graphql';

const viewerFragment = graphql`
    fragment useCoreBuyerIncentives_viewer on Viewer
    @argumentDefinitions(userCountryCode: { type: "String!" }) {
        isCountryInEu(country: $userCountryCode)
    }
`;

const itemFragment = graphql`
    fragment useCoreBuyerIncentives_item on Item
    @argumentDefinitions(userCountryCode: { type: "String!" }, userId: { type: "String" }) {
        userReturnPolicy: returnPolicy(userId: $userId) {
            returnDays(userCountryCode: $userCountryCode, includeEuReturns: true)
        }
    }
`;

const userFragment = graphql`
    fragment useCoreBuyerIncentives_user on User
    @argumentDefinitions(isTrade: { type: "Boolean!" }) {
        tradeFirm @include(if: $isTrade) {
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                    childPrograms(filter: "ENHANCED_BUYER_PROTECTION") {
                        code
                    }
                }
            }
        }
    }
`;

const isEUEligible = (viewer: useCoreBuyerIncentives_viewer$data): boolean => {
    const userCountryCode = getUserSessionCountryCode();
    return viewer.isCountryInEu || userCountryCode === 'GB';
};

const getReturnPolicyDuration = ({
    viewer,
    item,
}: {
    viewer: useCoreBuyerIncentives_viewer$data;
    item: useCoreBuyerIncentives_item$data | null;
}): number => {
    if (!item) {
        //when item is not provided rely on EU or ROW return policy
        return isEUEligible(viewer) ? 14 : 7;
    }

    /**
     * When item is provided rely on item return policy, it takes into account:
     * - EU or ROW return policy
     * - item return policy
     * - trade return policy
     *
     * By default it applies the longest available return policy
     */
    return item?.userReturnPolicy?.returnDays || 0;
};

const AuthenticityGuarantee: FC = () => (
    <FormattedMessage
        id="useCoreBuyerIncentives.authenticityGuarantee"
        defaultMessage="Authenticity Guarantee"
    />
);

const BuyerProtection: FC<{ days: number }> = ({ days }) => (
    <FormattedMessage
        id="useCoreBuyerIncentives.buyerProtection"
        defaultMessage="{days}-Day Buyer Protection"
        values={{ days }}
    />
);

const Returns: FC<{ days: number }> = ({ days }) => (
    <FormattedMessage
        id="useCoreBuyerIncentives.returns"
        defaultMessage="{days}-Day Returns"
        values={{ days }}
    />
);

const MIN_RETURN_POLICY_DURATION_TO_DISPLAY = 7;

const Cancellation: FC = () => (
    <FormattedMessage
        id="useCoreBuyerIncentives.cancellation"
        defaultMessage="24-Hour Cancellation"
    />
);

export const useCoreBuyerIncentives = ({
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
}: {
    viewer: useCoreBuyerIncentives_viewer$key;
    item: useCoreBuyerIncentives_item$key | null;
    user: useCoreBuyerIncentives_user$key | null;
}): [ReactNode, ReactNode, ReactNode] => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const tradeProgram = user?.tradeFirm?.rewards?.currentProgram;
    const tradeTier = tradeProgram?.programInfo?.code;
    const buyerProtectionDuration = getBuyerProtectionDuration(
        tradeProgram?.childPrograms?.[0]?.code
    );
    const returnPolicyDuration = getReturnPolicyDuration({ viewer, item });

    return useMemo(() => {
        const defaultIncentives: [ReactNode, ReactNode, ReactNode] = [
            <AuthenticityGuarantee />,
            <BuyerProtection days={buyerProtectionDuration} />,
            <Cancellation />,
        ];

        if (!returnPolicyDuration || returnPolicyDuration < MIN_RETURN_POLICY_DURATION_TO_DISPLAY) {
            return defaultIncentives;
        } else if (tradeTier === 'SILVER' || tradeTier === 'GOLD' || tradeTier === 'PLATINUM') {
            return [
                <AuthenticityGuarantee />,
                <BuyerProtection days={buyerProtectionDuration} />,
                <Returns days={returnPolicyDuration} />,
            ];
        } else if (tradeTier === 'MEMBER' || tradeTier === 'BRONZE' || isEUEligible(viewer)) {
            return [
                <AuthenticityGuarantee />,
                <Returns days={returnPolicyDuration} />,
                <Cancellation />,
            ];
        }

        return defaultIncentives;
    }, [buyerProtectionDuration, tradeTier, returnPolicyDuration, viewer]);
};
